import React, { useEffect, useMemo, useRef, useState } from "react";
import { format } from "date-fns";
import { useTranslation } from "react-i18next";
import {
  Copy,
  CopyPlus,
  CreditCard,
  MoreVertical,
  Pencil,
  Plus,
} from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Column, DataTable } from "@/components/ui/data-table";
import { DateRangePicker } from "@/components/common/DateRangePicker";
import { DateRange } from "@/hooks/useDateRange";
import { useAuthenticatedUser } from "@/hooks/useAuthenticatedUser";
import { useSorting } from "@/hooks/useSorting";
import {
  CatalogTenant,
  TenantCatalogSortField,
  TenantFilter,
} from "@/types/tenant-catalog";
import { useTenants } from "./hooks/useTenants";
import { TenantCreateDialog } from "./components/TenantCreateDialog";
import { TenantSubscriptionsDialog } from "./components/TenantSubscriptionsDialog";
import { TenantCopyDialog } from "./components/TenantCopyDialog";
import {
  hasPlatformPermission,
  isGuesteriaAdminRole,
  isSuperAdminRole,
} from "@/utils/accessControl";

const perPage = 10;
const debounceMs = 400;

const isTenantSortField = (field: string): field is TenantCatalogSortField =>
  field === "createdAt" || field === "name";

const ACTIVE_SUBSCRIPTION_STATUSES = new Set(["active", "trialing"]);

function uniquePlanNames(
  planNames: Array<string | null | undefined>,
): string[] {
  const seen = new Set<string>();

  return planNames.reduce<string[]>((acc, planName) => {
    const normalizedPlanName = planName?.trim();

    if (!normalizedPlanName) {
      return acc;
    }

    const key = normalizedPlanName.toLowerCase();

    if (seen.has(key)) {
      return acc;
    }

    seen.add(key);
    acc.push(normalizedPlanName);
    return acc;
  }, []);
}

function getTenantPlanNames(tenant: CatalogTenant): string[] {
  if (Array.isArray(tenant.tenantSubscriptions)) {
    return uniquePlanNames(
      tenant.tenantSubscriptions
        .filter(
          (s) =>
            !s.cancelAtPeriodEnd &&
            ACTIVE_SUBSCRIPTION_STATUSES.has(s.status.toLowerCase()),
        )
        .map((s) => s.plan.title),
    );
  }

  if (
    Array.isArray(tenant.subscriptionPlans) &&
    tenant.subscriptionPlans.length > 0
  ) {
    return uniquePlanNames(tenant.subscriptionPlans);
  }

  if (
    Array.isArray(tenant.subscriptionPlanNames) &&
    tenant.subscriptionPlanNames.length > 0
  ) {
    return uniquePlanNames(tenant.subscriptionPlanNames);
  }

  return uniquePlanNames([tenant.subscriptionPlan]);
}

function useDebounced<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    timerRef.current = setTimeout(() => setDebounced(value), delay);
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [value, delay]);

  return debounced;
}

export const TenantsPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { userProfile } = useAuthenticatedUser();
  const { sortField, sortDirection, handleSort } = useSorting();
  const [currentPage, setCurrentPage] = useState(1);
  const [subscriptionTenant, setSubscriptionTenant] =
    useState<CatalogTenant | null>(null);
  const [copyTenant, setCopyTenant] = useState<CatalogTenant | null>(null);
  const [createOpen, setCreateOpen] = useState(false);

  const [name, setName] = useState("");
  const [regionName, setRegionName] = useState("");
  const [subscriptionPlanId, setSubscriptionPlanId] = useState("");
  const [dateRange, setDateRange] = useState<DateRange>({
    from: undefined,
    to: undefined,
  });
  const [statusFilter, setStatusFilter] = useState<
    "all" | "Active" | "Suspended"
  >("all");

  const debouncedName = useDebounced(name, debounceMs);
  const debouncedRegionName = useDebounced(regionName, debounceMs);
  const debouncedSubscriptionPlanId = useDebounced(
    subscriptionPlanId,
    debounceMs,
  );
  const isSuperAdmin = isSuperAdminRole(userProfile);
  const isGuesteriaAdmin = isGuesteriaAdminRole(userProfile);
  const isPlatformAdmin = isSuperAdmin || isGuesteriaAdmin;
  const canReadTenants =
    isPlatformAdmin &&
    hasPlatformPermission(userProfile, "platform:tenants:read");
  const canAdministrateTenants =
    isPlatformAdmin &&
    hasPlatformPermission(userProfile, "platform:tenants:administrate");

  const sortBy = useMemo(() => {
    if (!sortField || !sortDirection) {
      return { sortBy: undefined, sortOrder: undefined };
    }
    if (!isTenantSortField(sortField)) {
      return { sortBy: undefined, sortOrder: undefined };
    }
    return { sortBy: sortField, sortOrder: sortDirection };
  }, [sortField, sortDirection]);

  const listQuery: TenantFilter = useMemo(
    () => ({
      name: debouncedName.trim() || undefined,
      status: statusFilter === "all" ? undefined : statusFilter,
      regionName: debouncedRegionName.trim() || undefined,
      subscriptionPlanId: debouncedSubscriptionPlanId.trim() || undefined,
      createdAfter: dateRange.from ?? undefined,
      createdBefore: dateRange.to ?? undefined,
      limit: perPage,
      offset: (currentPage - 1) * perPage,
      sortBy: sortBy.sortBy,
      sortOrder: sortBy.sortOrder,
    }),
    [
      currentPage,
      dateRange.from,
      dateRange.to,
      debouncedName,
      debouncedRegionName,
      debouncedSubscriptionPlanId,
      sortBy.sortBy,
      sortBy.sortOrder,
      statusFilter,
    ],
  );

  const { data, isLoading } = useTenants(listQuery, canReadTenants);

  const rows = data?.items ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / perPage));

  useEffect(() => {
    setCurrentPage(1);
  }, [
    debouncedName,
    debouncedRegionName,
    debouncedSubscriptionPlanId,
    dateRange.from,
    dateRange.to,
    statusFilter,
  ]);

  const getStatusVariant = (status: string) => {
    if (status === "Active") {
      return "default";
    }
    if (status === "Suspended") {
      return "destructive";
    }
    return "secondary";
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case "pending":
        return t("tenants.statuses.pending");
      case "Active":
        return t("tenants.statuses.active");
      case "Suspended":
        return t("tenants.statuses.suspended");
      default:
        return status;
    }
  };

  const columns: Column<CatalogTenant>[] = [
    {
      key: "name",
      header: t("tenants.table.name"),
      sortable: true,
    },
    {
      key: "slug",
      header: t("tenants.table.slug"),
      className: "text-center",
    },
    {
      key: "status",
      header: t("tenants.table.status"),
      className: "text-center",
      sortable: false,
      render: (value: string) => (
        <Badge variant={getStatusVariant(value)}>{getStatusLabel(value)}</Badge>
      ),
    },
    {
      key: "defaultRegion",
      header: t("tenants.table.region"),
      className: "text-center",
    },
    {
      key: "subscriptionPlan",
      header: t("tenants.table.subscriptionPlan"),
      className: "min-w-[180px] max-w-[280px]",
      render: (_value: string | null, row: CatalogTenant) => {
        const planNames = getTenantPlanNames(row);

        if (planNames.length === 0) {
          return <span className="text-muted-foreground text-sm">—</span>;
        }

        return (
          <div className="flex flex-wrap gap-1">
            {planNames.map((planName, index) => (
              <Badge key={`${planName}-${index}`} variant="new_secondary">
                {planName}
              </Badge>
            ))}
          </div>
        );
      },
    },
    {
      key: "createdAt",
      header: t("tenants.table.createdAt"),
      className: "text-center",
      sortable: true,
      render: (value: string) => (
        <span className="text-sm">
          {format(new Date(value), "dd MMM yyyy HH:mm")}
        </span>
      ),
    },
    {
      key: "id",
      header: "",
      className: "text-right w-[72px]",
      render: (_: string, row: CatalogTenant) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
            <Button
              variant="ghost"
              size="icon"
              type="button"
              aria-label={t("tenants.table.moreActions")}
            >
              <MoreVertical className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {isSuperAdmin ? (
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  setSubscriptionTenant(row);
                }}
              >
                <CreditCard className="h-4 w-4" />
                {t("tenants.table.manageSubscription")}
              </DropdownMenuItem>
            ) : null}
            <DropdownMenuItem
              onClick={(e) => {
                e.stopPropagation();
                void (async () => {
                  try {
                    await navigator.clipboard.writeText(row.id);
                    toast.success(t("tenants.table.copyIdSuccess"));
                  } catch {
                    toast.error(t("tenants.table.copyIdError"));
                  }
                })();
              }}
            >
              <Copy className="h-4 w-4" />
              {t("tenants.table.copyTenantId")}
            </DropdownMenuItem>
            {isSuperAdmin ? (
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  setCopyTenant(row);
                }}
              >
                <CopyPlus className="h-4 w-4" />
                {t("tenants.table.copyTenant")}
              </DropdownMenuItem>
            ) : null}
            <DropdownMenuItem
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/tenants/${row.id}/edit`);
              }}
              disabled={!canAdministrateTenants}
            >
              <Pencil className="h-4 w-4" />
              {t("tenants.table.editTenant")}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  return (
    <div className="mx-auto flex max-w-[1344px] flex-col space-y-6 p-12">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <Input
            placeholder={t("tenants.filters.name")}
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <Input
            placeholder={t("tenants.filters.regionName")}
            value={regionName}
            onChange={(e) => setRegionName(e.target.value)}
          />
          <Input
            placeholder={t("tenants.filters.subscriptionPlanId")}
            value={subscriptionPlanId}
            onChange={(e) => setSubscriptionPlanId(e.target.value)}
          />
          <DateRangePicker
            dateRange={dateRange}
            onDateRangeChange={setDateRange}
            className="!border-base-border !h-10 !min-h-10 !rounded-md !border !text-sm !font-normal shadow-xs"
          />
          <Select
            value={statusFilter}
            onValueChange={(value) =>
              setStatusFilter(value as "all" | "Active" | "Suspended")
            }
          >
            <SelectTrigger>
              <SelectValue placeholder={t("tenants.filters.status")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("tenants.filters.all")}</SelectItem>
              <SelectItem value="Active">
                {t("tenants.statuses.active")}
              </SelectItem>
              <SelectItem value="Suspended">
                {t("tenants.statuses.suspended")}
              </SelectItem>
            </SelectContent>
          </Select>
        </div>

        <Button
          variant="outline"
          onClick={() => setCreateOpen(true)}
          disabled={!canAdministrateTenants}
          title={
            !canAdministrateTenants
              ? t("common.insufficientPermissions", "Insufficient permissions")
              : undefined
          }
        >
          <Plus className="h-4 w-4" />
          {t("tenants.create.button")}
        </Button>
      </div>

      <DataTable<CatalogTenant>
        data={rows}
        columns={columns}
        loading={isLoading}
        emptyMessage={
          isGuesteriaAdmin
            ? t(
                "tenants.table.emptyGuesteriaAdmin",
                "You don't have any assigned or created tenants yet. Ask a Super Admin to assign you, or register a new tenant.",
              )
            : t("tenants.table.empty")
        }
        pagination={{
          currentPage,
          totalPages,
          onPageChange: setCurrentPage,
        }}
        sorting={{
          field: sortField,
          direction: sortDirection,
          onSort: handleSort,
        }}
        onRowClick={
          canAdministrateTenants
            ? (row) => navigate(`/tenants/${row.id}/edit`)
            : undefined
        }
      />

      <TenantCreateDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
      />
      <TenantSubscriptionsDialog
        tenant={subscriptionTenant}
        onClose={() => setSubscriptionTenant(null)}
      />
      <TenantCopyDialog
        tenant={copyTenant}
        onClose={() => setCopyTenant(null)}
      />
    </div>
  );
};
